import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { MdErrorOutline as NoChapterContentIcon } from "react-icons/md";
import scrollIntoView from "scroll-into-view-if-needed";
import { I18n } from "@aws-amplify/core";

import { MAGIC_STYLED_APP_BAR_MOBILE_GRADIENT_START_PX_NUMBER } from "../../AppBar/AppBar";
import Loader from "../../UI/Loader";
import ScriptureView from "../../ScriptureView/ScriptureView";
import Media from "../../../utils/media";
import { useBible } from "../../Bible/BibleContainer";
import { StateChapter, useChapter } from "../ChapterContainer";
import { withAudio, AudioCtx } from "../ChapterAudio/ChapterAudio";
import CloudOffIcon from "icons/CloudOffIcon";
import { useScriptureRouting } from "components/Bible/useScriptureRouting";
import { useNetworkStatus } from "utils/hooks";
import { useFums } from "utils/fums";

const StyledEmptyContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 300px;
  margin: 0 auto;
  svg {
    margin: auto;
    fill: ${({ theme }) => (theme.darkMode ? "#666" : "#e8e8e8")};
  }
  color: ${({ theme }) => (theme.darkMode ? "#666" : "#e8e8e8")};
`;

const ChapterCol = styled.div<{ touchEnabled: boolean }>`
  width: 100%;
  position: relative;
  flex: 1;
  padding: 4px 12px;
  padding-bottom: ${props =>
    props.touchEnabled
      ? "80px"
      : "20px"}; // for showing copyright info on initial scroll to bottom

  ${Media.desktopEmbedSafe`
    padding: 12px 12px 70px 12px;
    overflow-y: initial;
  `};
`;

const StyledChapterView = styled.div<{
  touchEnabled: boolean;
  hasAudio: boolean;
}>`
  width: 100%;
  display: flex;
  padding-top: ${MAGIC_STYLED_APP_BAR_MOBILE_GRADIENT_START_PX_NUMBER}px;
  box-sizing: border-box;
  flex-direction: ${props =>
    props.theme.scriptDirection === "RTL" ? "row-reverse" : "row"};
  ${props =>
    props.touchEnabled
      ? `height: 100vh;\n` +
        `height: calc(var(--vh, 1vh) * 100 - ${props.hasAudio ? 84 : 0}px);`
      : `height: auto;`};
  // overflow-y: scroll;
  ${props =>
    !props.touchEnabled && props.hasAudio ? "margin-bottom: 72px;" : ""}
  overflow-y: ${props => (!props.touchEnabled ? "initial" : "scroll")};
  -webkit-overflow-scrolling: touch;
  ${Media.desktopEmbedSafe`
    height: auto;
    margin-bottom: unset;
    overflow-y: initial;
    `};
`;

const StyledBibleVersion = styled.div`
  margin: 10px auto;
  font-family: Noto Serif;
  font-style: normal;
  font-weight: bold;
  line-height: normal;
  font-size: 0.7rem;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${props => (props.theme.darkMode ? "white" : "#9D9D9D")};
`;

const StyledCurrentChapter = styled.div`
  display: flex;
  align-items: bottom;
  justify-content: center;
  margin-top: 10px;
  font-family: Noto Serif;
  font-style: normal;
  font-weight: bold;
  line-height: normal;
  font-size: 1.125rem;
  line-height: 1;
  text-align: center;
  height: 25px;
  color: ${props => (props.theme.darkMode ? "white" : "black")};
`;

const Copyright = styled.div`
  text-align: center;
  font-family: ${props => props.theme.uiFont};
  font-size: 0.7em;
  padding: 10px 20px 2px;
  ${Media.desktop`
    padding: 30px 40px 2px;
  `};
  a {
    color: #666;
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }
`;

const PrivacyAndTOC = styled.div`
  display: block;
  margin-top: 8px;
  ${Media.desktop`
  display: none;
  margin-top: 0px;
  `};
`;

interface ChapterInfoProps {
  bibleVersion?: string;
  currentChapter: string;
}

const ChapterInfo = ({ bibleVersion, currentChapter }: ChapterInfoProps) => {
  return (
    <>
      {bibleVersion && <StyledBibleVersion>{bibleVersion}</StyledBibleVersion>}
      <StyledCurrentChapter>{currentChapter}</StyledCurrentChapter>
    </>
  );
};

interface ChapterViewProps {
  chapterId?: string;
  audio: AudioCtx;
  touchEnabled: boolean;
}

const ChapterView = ({
  touchEnabled = false,
  audio,
  chapterId: chapterIdOverride
}: ChapterViewProps) => {
  const { biblesById } = useBible();
  const chapterCtx = useChapter();
  const {
    bibleId: primaryBibleId,
    chapterId: routeChapterId,
    parallelBibleIds
  } = useScriptureRouting();
  const isOnline = useNetworkStatus();

  const chapterId = chapterIdOverride || routeChapterId;
  const isCurrentChapter = chapterIdOverride
    ? chapterIdOverride === routeChapterId
    : true;

  // scroll current passage into view if one is present
  const [hasScrolledToMatch, setHasScrolledToMatch] = useState(false);
  const primaryChapter = chapterCtx.getChapter({
    bibleId: primaryBibleId,
    chapterId
  });
  const primaryChapterLoaded = primaryChapter && biblesById[primaryBibleId];
  const chapterViewRef = useCallback(
    node => {
      if (node !== null && !hasScrolledToMatch && primaryChapterLoaded) {
        const firstPassageEl = node.querySelector(".matched");
        if (firstPassageEl) {
          setHasScrolledToMatch(true);
          scrollIntoView(firstPassageEl, {
            behavior: "smooth",
            scrollMode: "if-needed",
            block: "center"
          });
        }
      }
    },
    [primaryChapterLoaded, hasScrolledToMatch]
  );

  const bibleIds = [primaryBibleId, ...parallelBibleIds];
  const currentAudioChapter = audio.getCurrentAudioChapter();

  const chaptersByBibleId = bibleIds.reduce(
    (acc, bibleId) => ({
      ...acc,
      [bibleId]: chapterCtx.getChapter({ bibleId, chapterId })
    }),
    {} as { [bibleId: string]: StateChapter | undefined }
  );

  // only report visible chapters, some chapters can be rendered, but not shown
  // (i.e. mobile devices have pre/next chapters off screen, awaiting to be swiped into view)
  const visibleFumsIds = Object.values(chaptersByBibleId)
    .filter(chapter => chapter && chapter.id === routeChapterId)
    .map(chapter => chapter?.fumsToken) as string[];

  useFums(visibleFumsIds);

  return (
    <StyledChapterView
      id={isCurrentChapter ? "current-chapter" : ""}
      touchEnabled={touchEnabled}
      hasAudio={audio.isControllerOpen && audio.isPlayable}
      ref={chapterViewRef}
    >
      {bibleIds.map((bibleId, index) => {
        const isPrimaryBible = bibleId === primaryBibleId;
        const currentBible = biblesById[bibleId];
        const currentChapter = chaptersByBibleId[bibleId];
        const currentChapterRef = currentChapter
          ? currentChapter.reference.replace(" ", "-")
          : "";
        const currentBibleMeta = currentBible?.meta || {};

        const chapterUid = bibleId + ":" + chapterId;
        const { loading, error } = chapterCtx.chapterStatuses[chapterUid] || {};

        return (
          <ChapterCol
            id={isCurrentChapter && isPrimaryBible ? "current-chapter-col" : ""}
            key={chapterUid + ":" + index}
            touchEnabled={touchEnabled}
          >
            {currentChapter && biblesById[currentChapter.bibleId] && (
              <>
                {currentChapter && !currentChapter.id.includes("intro") && (
                  <ChapterInfo
                    bibleVersion={biblesById[bibleId]?.nameLocal}
                    currentChapter={currentChapter.reference}
                  />
                )}
                <ScriptureView
                  bibleId={bibleId}
                  disableFootnotes={currentBibleMeta.disableFootnotes}
                  currentChapterRef={currentChapterRef}
                  scriptDirection={
                    (currentBible && currentBible.language?.scriptDirection) ||
                    "LTR"
                  }
                  content={currentChapter.content || []}
                  highlightSelectedVerse
                  showVerseActions
                />
                <Copyright>
                  <Link to={`/bible/${bibleId}/info`}>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: currentChapter.copyright || ""
                      }}
                    />
                    {isPrimaryBible && currentAudioChapter && (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: currentAudioChapter.copyright
                        }}
                      />
                    )}
                  </Link>
                  {isPrimaryBible && (
                    <PrivacyAndTOC>
                      <Link to="/privacy">{I18n.get("Privacy")}</Link> |{" "}
                      <Link to="/terms">
                        {I18n.get("Terms and Conditions")}
                      </Link>
                    </PrivacyAndTOC>
                  )}
                </Copyright>
              </>
            )}

            {!currentChapter && error && (
              <StyledEmptyContent>
                <NoChapterContentIcon style={{ fontSize: 128 }} />
              </StyledEmptyContent>
            )}

            {!currentChapter && loading && (
              <StyledEmptyContent>
                <Loader size={128} />
              </StyledEmptyContent>
            )}

            {!currentChapter && !error && !loading && !isOnline && (
              <StyledEmptyContent>
                <CloudOffIcon size={128} />
              </StyledEmptyContent>
            )}
          </ChapterCol>
        );
      })}
    </StyledChapterView>
  );
};

export default withAudio(ChapterView);
